import React from 'react';

import { CardTypeIcon } from '../../common/cardStats/CardTypeIcon';
import { formatInt, formatNumber, formatPct } from '../../common/cardStats/formatters';
import { synergyEnumLabel } from '../../../data/deckSynergyProfileMarkers';
import {
    SYNERGY_CATEGORY_ORDER as CATEGORY_ORDER,
    SYNERGY_CATEGORY_SHORT_LABELS as CATEGORY_SHORT_LABELS,
    synergyCategoryColor,
} from '../../../data/synergyCategoryConstants';

/**
 * `profile.density` / `reach` / `enablerDependence` use the shared Density
 * enum — reuse the powerFacetDensity pill styling (light/moderate/dense)
 * so the synergy fingerprint matches the power-level one visually.
 */
function DensityPill({ density }) {
    if (!density || density === 'none') return null;
    const label = synergyEnumLabel('density', density);
    return (
        <span className={`powerFacetDensity powerFacetDensity--${density}`}>{label}</span>
    );
}

function FacetHeader({ title, density, headline }) {
    return (
        <div className="powerFacetHeader">
            <div className="powerFacetTitleRow">
                <span className="powerFacetTitle">{title}</span>
                <DensityPill density={density} />
            </div>
            {headline && <span className="powerFacetHeadline">{headline}</span>}
        </div>
    );
}

function StatRow({ stats }) {
    const visible = (stats || []).filter((s) => s && s.value != null && s.value !== '');
    if (visible.length === 0) return null;
    return (
        <div className="powerFacetStats">
            {visible.map((s, i) => (
                <span key={i} className="powerFacetStat">
                    <strong>{s.value}</strong> {s.label}
                </span>
            ))}
        </div>
    );
}

function MixFacet({ mix, tempo }) {
    if (!mix) return null;
    const total = CATEGORY_ORDER.reduce((sum, k) => sum + (mix[k]?.score || 0), 0);
    const segments = CATEGORY_ORDER.map((k) => {
        const m = mix[k];
        if (!m || !m.score) return null;
        return {
            key: k,
            label: CATEGORY_SHORT_LABELS[k],
            color: synergyCategoryColor(k),
            score: m.score,
            share: total > 0 ? m.score / total : 0,
            entries: m.entries || 0,
        };
    }).filter(Boolean);
    if (segments.length === 0) return null;

    const headline = synergyEnumLabel('tempo', tempo);

    return (
        <div className="powerFacet powerFacet--wide">
            <FacetHeader title="Category mix" headline={headline} />
            <div className="powerFacetStackedBar">
                {segments.map((s) => (
                    <div
                        key={s.key}
                        className="powerFacetStackedSeg"
                        style={{
                            width: `${Math.max(s.share * 100, 2)}%`,
                            background: s.color,
                            color: '#0a0a0a',
                        }}
                        title={`${s.label} — ${formatInt(s.score)} pts · ${s.entries} entries`}
                    >
                        {s.share >= 0.14 && <span>{formatPct(s.share)}</span>}
                    </div>
                ))}
            </div>
            <div className="synergyMixLegend">
                {segments.map((s) => (
                    <div key={s.key} className="synergyMixLegendItem">
                        <span
                            className="synergyMixLegendSwatch"
                            style={{ background: s.color }}
                        />
                        <span className="synergyMixLegendLabel">{s.label}</span>
                        <span className="synergyMixLegendValue">
                            {formatPct(s.share)} · {s.entries} ent
                        </span>
                    </div>
                ))}
            </div>
        </div>
    );
}

function ShapeFacet({ profile, onOpenWeb }) {
    if (!profile) return null;
    const concentrationLabel = synergyEnumLabel('concentration', profile.concentration);
    const topologyLabel = synergyEnumLabel('webTopology', profile.webTopology);
    const stats = [
        profile.gini != null
            ? { value: profile.gini.toFixed(2), label: 'gini' }
            : null,
        profile.topConditionShare != null
            ? { value: formatPct(profile.topConditionShare), label: 'top entry' }
            : null,
        profile.topFiveShare != null
            ? { value: formatPct(profile.topFiveShare), label: 'top 5' }
            : null,
    ];
    const headline = [topologyLabel, concentrationLabel]
        .filter(Boolean)
        .join(' · ');
    const clickable = typeof onOpenWeb === 'function';
    const body = (
        <>
            <FacetHeader title="Web shape" headline={headline} />
            <StatRow stats={stats} />
            {clickable && (
                <span className="synergyShapeFacetCta">
                    <span className="synergyShapeFacetCtaIcon" aria-hidden="true">
                        <svg viewBox="0 0 24 24" width="14" height="14">
                            <circle cx="12" cy="12" r="2.2" fill="currentColor" />
                            <circle cx="5" cy="7" r="1.5" fill="currentColor" />
                            <circle cx="19" cy="7" r="1.5" fill="currentColor" />
                            <circle cx="5" cy="17" r="1.5" fill="currentColor" />
                            <circle cx="19" cy="17" r="1.5" fill="currentColor" />
                            <line x1="12" y1="12" x2="5" y2="7" stroke="currentColor" strokeWidth="1.2" />
                            <line x1="12" y1="12" x2="19" y2="7" stroke="currentColor" strokeWidth="1.2" />
                            <line x1="12" y1="12" x2="5" y2="17" stroke="currentColor" strokeWidth="1.2" />
                            <line x1="12" y1="12" x2="19" y2="17" stroke="currentColor" strokeWidth="1.2" />
                        </svg>
                    </span>
                    <span className="synergyShapeFacetCtaLabel">Open interactive web</span>
                    <span className="synergyShapeFacetCtaArrow" aria-hidden="true">→</span>
                </span>
            )}
        </>
    );
    if (!clickable) {
        return <div className="powerFacet">{body}</div>;
    }
    return (
        <button
            type="button"
            className="powerFacet synergyShapeFacetButton"
            onClick={onOpenWeb}
            aria-label="Open interactive synergy web"
        >
            {body}
        </button>
    );
}

function ReachFacet({ profile }) {
    if (!profile) return null;
    const participating = profile.participatingCards || 0;
    const orphan = profile.orphanCards || 0;
    const nonland = participating + orphan;
    const coveragePct = profile.coverage != null ? profile.coverage : null;
    const stats = [
        { value: participating, label: 'participating' },
        orphan > 0 ? { value: orphan, label: 'orphans' } : null,
        profile.entryCount ? { value: profile.entryCount, label: 'entries' } : null,
    ];
    return (
        <div className="powerFacet">
            <FacetHeader
                title="Reach"
                density={profile.reach}
                headline={
                    coveragePct != null
                        ? `${formatPct(coveragePct)} of ${nonland || '—'} nonland cards touch the web`
                        : null
                }
            />
            <StatRow stats={stats} />
        </div>
    );
}

function CommanderFacet({ profile }) {
    if (!profile) return null;
    const centricity = profile.commanderCentricity;
    if (!centricity && !profile.commanderShare && !profile.commanderBonusShare) return null;
    const centricityLabel = synergyEnumLabel('commanderCentricity', centricity);
    const stats = [
        profile.commanderShare != null
            ? { value: formatPct(profile.commanderShare), label: 'of score' }
            : null,
        profile.commanderBonusShare != null && profile.commanderBonusShare > 0
            ? { value: formatPct(profile.commanderBonusShare), label: 'bonus share' }
            : null,
    ];
    return (
        <div className="powerFacet">
            <FacetHeader title="Commander" headline={centricityLabel} />
            <StatRow stats={stats} />
        </div>
    );
}

function SupportFacet({ profile }) {
    if (!profile) return null;
    const redundancyLabel = synergyEnumLabel('redundancy', profile.redundancy);
    const stats = [
        profile.avgSupportersPerEntry != null
            ? { value: formatNumber(profile.avgSupportersPerEntry, 1), label: 'sup / entry' }
            : null,
        profile.avgEnablersPerEntry != null
            ? { value: formatNumber(profile.avgEnablersPerEntry, 1), label: 'ena / entry' }
            : null,
        profile.enablerDependenceRatio != null && profile.enablerDependenceRatio > 0
            ? { value: formatPct(profile.enablerDependenceRatio), label: 'enabler-driven' }
            : null,
        profile.genericTypeShare != null && profile.genericTypeShare > 0
            ? { value: formatPct(profile.genericTypeShare), label: 'any-spell' }
            : null,
    ];
    return (
        <div className="powerFacet">
            <FacetHeader
                title="Support"
                density={profile.enablerDependence}
                headline={redundancyLabel}
            />
            <StatRow stats={stats} />
        </div>
    );
}

function AnchorRow({ anchor, card, onSelectCard }) {
    const name = card?.name || anchor.cardId;
    const imageUri = card?.imageUri;
    const types = card?.types;
    const handleClick = () => {
        if (onSelectCard && imageUri) onSelectCard({ ...card, id: anchor.cardId });
    };
    return (
        <div className="synergyFingerprintAnchorRow">
            {imageUri ? (
                <img
                    src={imageUri}
                    alt={name}
                    className="cardStatThumb"
                    onClick={handleClick}
                    loading="lazy"
                />
            ) : (
                <div className="cardStatThumbPlaceholder" />
            )}
            <CardTypeIcon types={types} />
            <span
                className="cardStatName"
                onClick={handleClick}
                style={{ cursor: imageUri ? 'pointer' : 'default' }}
                title={name}
            >
                {name}
            </span>
            <div className="synergyFingerprintAnchorMeta">
                <span
                    className="synergyFingerprintAnchorShare"
                    title="Share of total synergy score driven by this card"
                >
                    {formatPct(anchor.share || 0)}
                </span>
                <span
                    className="synergyFingerprintAnchorPts"
                    title="Total synergy points this card contributes"
                >
                    {`${formatInt(anchor.score || 0)} pts`}
                </span>
                <span
                    className="synergyFingerprintAnchorEntries"
                    title="Synergy entries owned by this card"
                >
                    {`${anchor.entryCount || 0} ent`}
                </span>
            </div>
        </div>
    );
}

function HubRow({ hub, card, onSelectCard }) {
    const name = card?.name || hub.cardId;
    const imageUri = card?.imageUri;
    const types = card?.types;
    const handleClick = () => {
        if (onSelectCard && imageUri) onSelectCard({ ...card, id: hub.cardId });
    };
    return (
        <div className="synergyFingerprintAnchorRow">
            {imageUri ? (
                <img
                    src={imageUri}
                    alt={name}
                    className="cardStatThumb"
                    onClick={handleClick}
                    loading="lazy"
                />
            ) : (
                <div className="cardStatThumbPlaceholder" />
            )}
            <CardTypeIcon types={types} />
            <span
                className="cardStatName"
                onClick={handleClick}
                style={{ cursor: imageUri ? 'pointer' : 'default' }}
                title={name}
            >
                {name}
            </span>
            <div className="synergyFingerprintAnchorMeta">
                <span
                    className="synergyFingerprintAnchorShare"
                    title="Times this card is referenced across other entries"
                >
                    {`${hub.connections || 0} refs`}
                </span>
            </div>
        </div>
    );
}

export function SynergyFingerprint({ profile, cards, onSelectCard, onOpenWeb }) {
    if (!profile) return null;
    const anchors = Array.isArray(profile.anchors) ? profile.anchors : [];
    const hubs = Array.isArray(profile.hubs) ? profile.hubs : [];

    return (
        <>
            <div className="powerFacetGrid">
                <MixFacet mix={profile.mix} tempo={profile.tempo} />
                <ReachFacet profile={profile} />
                <ShapeFacet profile={profile} onOpenWeb={onOpenWeb} />
                <CommanderFacet profile={profile} />
                <SupportFacet profile={profile} />
            </div>

            {(anchors.length > 0 || hubs.length > 0) && (
                <div className="synergyFingerprintLists">
                    {anchors.length > 0 && (
                        <div className="synergyFingerprintList">
                            <div className="synergyFingerprintListHeader">
                                <span className="synergyFingerprintListTitle">Anchors</span>
                                <span className="synergyFingerprintListHint">
                                    Cards carrying the biggest share of synergy score
                                </span>
                            </div>
                            <div className="synergyFingerprintListBody">
                                {anchors.map((a) => (
                                    <AnchorRow
                                        key={`anchor_${a.cardId}`}
                                        anchor={a}
                                        card={cards?.[a.cardId]}
                                        onSelectCard={onSelectCard}
                                    />
                                ))}
                            </div>
                        </div>
                    )}
                    {hubs.length > 0 && (
                        <div className="synergyFingerprintList">
                            <div className="synergyFingerprintListHeader">
                                <span className="synergyFingerprintListTitle">Hubs</span>
                                <span className="synergyFingerprintListHint">
                                    Cards referenced most by other entries — connective tissue
                                </span>
                            </div>
                            <div className="synergyFingerprintListBody">
                                {hubs.map((h) => (
                                    <HubRow
                                        key={`hub_${h.cardId}`}
                                        hub={h}
                                        card={cards?.[h.cardId]}
                                        onSelectCard={onSelectCard}
                                    />
                                ))}
                            </div>
                        </div>
                    )}
                </div>
            )}
        </>
    );
}
